import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { humanize } from '@/lib/format';
import { admin_bot_actions_path } from '@/rails/routes';
import { router } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

export interface BotTool {
  tool: string;
  arguments: string[];
}

export interface BotCompany {
  id: string;
  name: string;
  source: string;
}

export interface BotApp {
  id: string;
  name: string;
  companyName: string;
}

interface ToolFormProps {
  tools: BotTool[];
  companies: BotCompany[];
  apps: BotApp[];
  plans: string[];
  templates: string[];
}

interface Choice {
  value: string;
  label: string;
}

const SOURCE_CHOICES: Choice[] = [
  { value: 'public', label: 'Public signup' },
  { value: 'caprock_client', label: 'Client job' },
];

const SOURCE_LABELS: Record<string, string> = {
  public: 'Public signup',
  caprock_client: 'Client job',
};

const ARGUMENT_LABELS: Record<string, string> = {
  name: 'Name',
  source: 'Source',
  company_id: 'Company',
  app_id: 'App',
  plan_key: 'Plan',
  template_key: 'Template',
};

// A new account is a public one unless staff say it is a client job.
const DEFAULT_VALUES: Record<string, string> = { source: 'public' };

const ToolForm = ({ tools, companies, apps, plans, templates }: ToolFormProps) => {
  const [toolName, setToolName] = useState(tools[0]?.tool ?? '');
  const [values, setValues] = useState<Record<string, string>>(DEFAULT_VALUES);
  const [busy, setBusy] = useState(false);

  const tool = tools.find((candidate) => candidate.tool === toolName);
  const toolArguments = tool?.arguments ?? [];
  const complete = toolArguments.every((name) => (values[name] ?? '').trim() !== '');

  // The choices for an argument the bot takes from a fixed set. Anything else is typed.
  const choicesFor = (name: string): Choice[] | null => {
    if (name === 'company_id') {
      return companies.map((company) => ({
        value: company.id,
        label: `${company.name} · ${SOURCE_LABELS[company.source] ?? humanize(company.source)}`,
      }));
    }
    if (name === 'app_id') {
      return apps.map((app) => ({ value: app.id, label: `${app.name} · ${app.companyName}` }));
    }
    if (name === 'plan_key') return plans.map((key) => ({ value: key, label: humanize(key) }));
    if (name === 'template_key') {
      return templates.map((key) => ({ value: key, label: humanize(key) }));
    }
    if (name === 'source') return SOURCE_CHOICES;

    return null;
  };

  const setValue = (name: string, value: string) => {
    setValues((current) => ({ ...current, [name]: value }));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (tool && complete) {
      router.post(
        admin_bot_actions_path(),
        {
          tool: tool.tool,
          arguments: Object.fromEntries(
            tool.arguments.map((name) => [name, (values[name] ?? '').trim()])
          ),
        },
        { onStart: () => setBusy(true), onFinish: () => setBusy(false) }
      );
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="bot-tool">Tool</Label>
        <Select value={toolName} onValueChange={setToolName}>
          <SelectTrigger id="bot-tool" className="w-full sm:w-72">
            <SelectValue placeholder="Pick a tool" />
          </SelectTrigger>
          <SelectContent>
            {tools.map((candidate) => (
              <SelectItem key={candidate.tool} value={candidate.tool}>
                <code className="text-xs">{candidate.tool}</code>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {toolArguments.map((name) => {
        const id = `bot-argument-${name}`;
        const label = ARGUMENT_LABELS[name] ?? humanize(name);
        const choices = choicesFor(name);

        return (
          <div key={name} className="space-y-2">
            <Label htmlFor={id}>
              {label} <code className="text-muted-foreground text-xs font-normal">{name}</code>
            </Label>
            {choices ? (
              <Select value={values[name] ?? ''} onValueChange={(value) => setValue(name, value)}>
                <SelectTrigger id={id} className="w-full sm:w-72">
                  <SelectValue placeholder={`Pick a ${label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((choice) => (
                    <SelectItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={id}
                className="sm:w-72"
                value={values[name] ?? ''}
                onChange={(event) => setValue(name, event.target.value)}
              />
            )}
          </div>
        );
      })}

      <Button type="submit" disabled={!tool || !complete || busy}>
        Propose step
      </Button>
    </form>
  );
};

export default ToolForm;
